// Talmia II background theme registry.
// Every option here is a CSS `background` value that layers on top of the
// solid navy base (#080E18). Used by BackgroundProvider to swap the rear-most
// background layer per major menu section.
//
// Order + labels mirror the design PDF Raj shared.

export const BASE_COLOR = "#080E18";

/* ---------------- Slick gradients (01–06 in PDF)  --------------- */
export const BACKGROUNDS = [
  {
    id: "07-aurora-navy",
    label: "Aurora Navy",
    tone: "Cool · violet drift",
    css:
      "radial-gradient(60% 60% at 25% 25%, #1B2944 0%, transparent 60%)," +
      "radial-gradient(50% 50% at 80% 20%, #2D3A6B 0%, transparent 65%)," +
      "radial-gradient(70% 70% at 50% 100%, #0F1730 0%, transparent 70%)",
  },
  {
    id: "08-sunrise-navy",
    label: "Sunrise Navy",
    tone: "Warm · orange to navy",
    css:
      "radial-gradient(55% 55% at 12% 83%, #B04A2A 0%, transparent 60%)," +
      "radial-gradient(60% 60% at 88% 10%, #1B2944 0%, transparent 65%)," +
      "radial-gradient(80% 80% at 50% 60%, #0F1730 0%, transparent 75%)",
  },
  {
    id: "09-mesh-cosmos",
    label: "Mesh Cosmos",
    tone: "Vibrant · four-point",
    css:
      "radial-gradient(35% 35% at 15% 15%, #1B4D6B 0%, transparent 55%)," +
      "radial-gradient(35% 35% at 85% 15%, #A73F1D 0%, transparent 55%)," +
      "radial-gradient(35% 35% at 15% 85%, #2D3A6B 0%, transparent 55%)," +
      "radial-gradient(35% 35% at 85% 85%, #E67E22 0%, transparent 55%)",
  },
  {
    id: "10-light-beam",
    label: "Light Beam",
    tone: "Calm · top-down spotlight",
    css:
      "linear-gradient(188deg, #44537F 0%, #1B2944 30%, #080E18 100%)",
  },
  {
    id: "11-horizon-glow",
    label: "Horizon Glow",
    tone: "Cinematic · bottom edge",
    css:
      "linear-gradient(185deg, #080E18 0%, #12172B 60%, #A63B33 130%)",
  },
  {
    id: "12-kidescent-sweep",
    label: "Kidescent Sweep",
    tone: "Corner to corner",
    css:
      "radial-gradient(55% 55% at 90% 90%, #1B2944 0%, transparent 65%)," +
      "radial-gradient(45% 45% at 15% 15%, #2D3A6B 0%, transparent 65%)," +
      "linear-gradient(160deg, #0B1226, #12233A 100%)",
  },

  /* ---------------- Textured feels (01–06 in PDF)  --------------- */
  {
    id: "01-deep-navy",
    label: "Deep Navy",
    tone: "Neutral · subtle bronze wash",
    css:
      "radial-gradient(80% 80% at 20% 10%, rgba(193,122,74,0.10) 0%, transparent 60%)," +
      "radial-gradient(70% 70% at 80% 100%, rgba(74,97,201,0.12) 0%, transparent 65%)," +
      "linear-gradient(180deg, #101736 0%, #0A1024 100%)",
  },
  {
    id: "02-navy-teal-hex",
    label: "Navy · Teal Hex",
    tone: "Hex lattice",
    // Approx CSS-only hex feel via layered conic gradients + subtle teal wash.
    css:
      "radial-gradient(70% 70% at 20% 20%, rgba(47,200,203,0.10) 0%, transparent 60%)," +
      "radial-gradient(60% 60% at 80% 80%, rgba(193,122,74,0.08) 0%, transparent 60%)," +
      "linear-gradient(180deg, #1B1C28 0%, #101828 100%)",
  },
  {
    id: "03-bronze-teal",
    label: "Bronze · Teal",
    tone: "Brushed copper",
    css:
      "radial-gradient(80% 60% at 50% 0%, rgba(193,122,74,0.16) 0%, transparent 55%)," +
      "radial-gradient(70% 70% at 50% 100%, rgba(47,200,203,0.10) 0%, transparent 65%)," +
      "linear-gradient(180deg, #1A1218 0%, #121018 100%)",
  },
  {
    id: "04-brass-glow-navy",
    label: "Brass Glow Navy",
    tone: "Warm diagonal sweep",
    css:
      "radial-gradient(90% 60% at 12% 20%, rgba(230,126,34,0.16) 0%, transparent 60%)," +
      "linear-gradient(160deg, #10142C 0%, #12233A 100%)",
  },
  {
    id: "05-navy-hex-tile",
    label: "Navy Hex Tile",
    tone: "Muted lattice",
    css:
      "radial-gradient(60% 60% at 30% 40%, rgba(30,58,138,0.20) 0%, transparent 60%)," +
      "radial-gradient(60% 60% at 70% 70%, rgba(15,23,42,0.4) 0%, transparent 65%)," +
      "linear-gradient(180deg, #0F1730 0%, #0A1024 100%)",
  },
  {
    id: "06-navy-orange",
    label: "Navy · Orange",
    tone: "Grainy · bottom light",
    css:
      "radial-gradient(80% 80% at 50% 110%, rgba(230,126,34,0.14) 0%, transparent 60%)," +
      "linear-gradient(180deg, #14182D 0%, #0F1428 100%)",
  },
];

export const BACKGROUNDS_BY_ID = Object.fromEntries(BACKGROUNDS.map((b) => [b.id, b]));

/* ---------------- Major menu sections ------------------------ */
export const SECTIONS = [
  { key: "boss",           label: "Boss Mode",       matches: ["/"] },
  { key: "sales",          label: "Sales & Orders",  matches: ["/quotes", "/deals", "/leads", "/sales"] },
  { key: "crm",            label: "CRM",             matches: ["/crm"] },
  { key: "marketing",      label: "Marketing",       matches: ["/marketing"] },
  { key: "communications", label: "Communications",  matches: ["/calls", "/whatsapp", "/email", "/communications"] },
  { key: "hub",            label: "Hub",             matches: ["/hub"] },
  { key: "lab",            label: "Data Lab",        matches: ["/lab", "/items"] },
  { key: "lara",           label: "Lara AI",         matches: ["/lara"] },
  { key: "admin",          label: "Admin & Config",  matches: ["/admin", "/profile", "/settings"] },
];

/* Defaults chosen by the design pass — user can override in /settings/backgrounds */
export const DEFAULT_SECTION_BG = {
  boss:           "10-light-beam",
  sales:          "11-horizon-glow",
  crm:            "04-brass-glow-navy",
  marketing:      "08-sunrise-navy",
  communications: "07-aurora-navy",
  hub:            "08-sunrise-navy",
  lab:            "12-kidescent-sweep",
  lara:           "09-mesh-cosmos",
  admin:          "01-deep-navy",
};

/* ---------------- Coordinated Theme Presets ---------------------
   One-click "paint every section" curated combos. `map` covers all 9
   section keys so applying a preset writes over the whole user map in
   one shot (no per-section clicking).
*/
export const THEME_PRESETS = [
  {
    id: "midnight-signal",
    label: "Midnight Signal",
    tagline: "Cool, calm, mission-control feel across the whole app.",
    accent: "#4A61C9",
    map: {
      boss:           "10-light-beam",
      sales:          "07-aurora-navy",
      crm:            "05-navy-hex-tile",
      marketing:      "02-navy-teal-hex",
      communications: "07-aurora-navy",
      hub:            "10-light-beam",
      lab:            "05-navy-hex-tile",
      lara:           "02-navy-teal-hex",
      admin:          "01-deep-navy",
    },
  },
  {
    id: "brass-warm",
    label: "Brass Warm",
    tagline: "Warm bronze accents — Construction Warehouse house feel.",
    accent: "#C17A4A",
    map: {
      boss:           "04-brass-glow-navy",
      sales:          "11-horizon-glow",
      crm:            "04-brass-glow-navy",
      marketing:      "08-sunrise-navy",
      communications: "03-bronze-teal",
      hub:            "06-navy-orange",
      lab:            "04-brass-glow-navy",
      lara:           "11-horizon-glow",
      admin:          "03-bronze-teal",
    },
  },
  {
    id: "cosmos-vibrant",
    label: "Cosmos Vibrant",
    tagline: "High-energy · four-point mesh gradients for launch days.",
    accent: "#E67E22",
    map: {
      boss:           "09-mesh-cosmos",
      sales:          "08-sunrise-navy",
      crm:            "12-kidescent-sweep",
      marketing:      "09-mesh-cosmos",
      communications: "07-aurora-navy",
      hub:            "08-sunrise-navy",
      lab:            "12-kidescent-sweep",
      lara:           "09-mesh-cosmos",
      admin:          "12-kidescent-sweep",
    },
  },
  {
    id: "slate-quiet",
    label: "Slate Quiet",
    tagline: "Muted · lowest visual noise for deep-focus days.",
    accent: "#64748B",
    map: {
      boss:           "01-deep-navy",
      sales:          "05-navy-hex-tile",
      crm:            "01-deep-navy",
      marketing:      "05-navy-hex-tile",
      communications: "01-deep-navy",
      hub:            "05-navy-hex-tile",
      lab:            "01-deep-navy",
      lara:           "05-navy-hex-tile",
      admin:          "01-deep-navy",
    },
  },
  {
    id: "designer-default",
    label: "Designer Default",
    tagline: "The mix Raj signed off at design time — restore anytime.",
    accent: "#2FC8CB",
    map: { ...DEFAULT_SECTION_BG },
  },
];

export const PRESETS_BY_ID = Object.fromEntries(THEME_PRESETS.map((p) => [p.id, p]));

export function sectionKeyFromPath(pathname) {
  // Longest-match wins so "/quotes" isn't accidentally caught by "/".
  let matched = { key: "boss", len: -1 };
  for (const s of SECTIONS) {
    for (const m of s.matches) {
      if (m === "/") {
        if (pathname === "/" && matched.len < 0) matched = { key: s.key, len: 0 };
      } else if (pathname === m || pathname.startsWith(m + "/") || pathname === m) {
        if (m.length > matched.len) matched = { key: s.key, len: m.length };
      }
    }
  }
  return matched.key;
}
